import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import { IAuthContext } from "../stores";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { USER_ROLES } from "../utils/constants";
import moment from "moment";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { IAuthUserDetail } from "../stores/auth";

export const AuthContext = createContext({} as IAuthContext);

interface IAuthContextProvider {
  children: ReactElement;
}

const cookieName = "token";

const AuthContextProvider = ({ children }: IAuthContextProvider) => {
  const navigate = useNavigate();
  const [cookies, setCookie, removeCookie] = useCookies();
  const [isAuthenticated, setAuthenticated] = useState(false);
  const [userId, setUserId] = useState<string>();
  const [userName, setUserName] = useState<string>("");
  const [firstName, setFirstName] = useState<string>();
  const [surname, setSurname] = useState<string>();
  const [isShopUser, setIsShopUser] = useState(false);
  const [isShopAdmin, setIsShopAdmin] = useState(false);
  const [isShopCustomer, setIsShopCustomer] = useState(false);
  const [isMobileResolution, setIsMobileResolution] = useState(false);
  const [isScrolledDown, setScrolledDown] = useState(false);

  const isTokenExpired = (token: string) => {
    let jwt = jwtDecode<JwtPayload>(token);
    if (!jwt.exp) return false;

    const expiryDate = new Date(jwt.exp * 1000);
    return expiryDate < new Date();
  };

  useEffect(() => {
    window.addEventListener("scroll", () => {
      if (window.scrollY < 15) {
        setScrolledDown(false);
      } else {
        setScrolledDown(true);
      }
    });
  }, []);

  useEffect(() => {
    if (cookies.token && isTokenExpired(cookies.token)) {
      clearAuth();
      return;
    }
    if (cookies.token && !isAuthenticated) setAuth(cookies.token);
  }, [isAuthenticated]);

  useEffect(() => {
    const isMobileScreen = window.innerWidth <= 1024;
    setIsMobileResolution(isMobileScreen);

    const handleResize = () => {
      setIsMobileResolution(window.innerWidth <= 1024);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const setAuth = (token: string) => {
    if (cookies.token) {
      removeCookie(cookieName);
    }

    let jwt = jwtDecode<IAuthUserDetail>(token);
    const expiryDate = jwt.exp
      ? new Date(jwt.exp * 1000)
      : moment().add(12, "months").toDate();

    setCookie(cookieName, token, { expires: expiryDate });

    setAuthenticated(true);
    setUserId(jwt.UserId);
    setUserName(jwt.UserName);
    setFirstName(jwt.FirstName);
    setSurname(jwt.Surname);
    setIsShopAdmin(jwt.Role === USER_ROLES.ShopAdmin);
    setIsShopUser(jwt.Role === USER_ROLES.ShopUser);
    setIsShopCustomer(jwt.Role === USER_ROLES.ShopCustomer);

    const interval = setInterval(() => {
      if (isTokenExpired(token)) {
        clearAuth();
        navigate("/");
      }
    }, 5000);

    return () => clearInterval(interval);
  };

  const clearAuth = () => {
    Object.keys(cookies).forEach((cookieName) => {
      removeCookie(cookieName);
    });
    setAuthenticated(false);
    setUserId(undefined);
    setUserName("");
    setIsShopAdmin(false);
    setIsShopUser(false);
    setIsShopCustomer(false);
    navigate("/");
  };

  return (
    <AuthContext.Provider
      value={{
        isScrolledDown,
        isMobileResolution,
        isAuthenticated,
        userId,
        userName,
        firstName,
        surname,
        isShopUser,
        isShopAdmin,
        isShopCustomer,
        setAuth,
        clearAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContextProvider;

export const useAuth = (): IAuthContext => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
